<template>
    <div>
        <v-card class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">Batch Copy {{ sourceFiscalYear }} Data into {{ targetFiscalYear }}</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>
            <v-card-text class="mb-0">
                <div :class="isMobile?'mr-20 ml-20 wp-90':'mr-20 ml-20 wp-50'">
                    <v-text-field v-model="search" append-icon="search" label="Search team, employee" single-line hide-details clearable></v-text-field>
                </div>
                <v-layout row>
                    <v-flex xs12 sm12>
                        <v-data-table
                            :headers="isMobile?header_mobile:header"
                            :items="allocation_history_filtered"
                            :rows-per-page-items="rowsPerPageItems"
                            :pagination.sync="paginationSync"
                        >
                            <template v-slot:items="props">
                                <tr @click="props.expanded = !props.expanded" class="clickable" title="Click to view options">
                                    <td>
                                        <v-chip class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.firstName}} {{props.item.lastName}}
                                        </v-chip>
                                    </td>
                                    <td v-if="!isMobile">
                                        <team-chips :teams="props.item.teams"></team-chips>
                                    </td>
                                    <td v-if="!isMobile">
                                        <b :class="commonfn.getStatusColor(props.item.employmentStatus)">{{commonfn.getStatusDescription(props.item.employmentStatus)}}</b>
                                    </td>
                                    <td>
                                        <b :class="commonfn.getStatusColor(props.item.allocationStatus)">{{commonfn.getStatusDescription(props.item.allocationStatus)}}</b>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-20">
                <v-spacer></v-spacer>
                <v-btn color="primary" :disabled="disabled||loading||noData" :loading="loading" dark @click="showBatchCopyConfirmation">Batch Copy</v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="dialogOkCancel" persistent width="700">
            <ok-cancel :msg="dialogOkCancelMsg" alignLeft="true" :dialogOkCancel.sync="dialogOkCancel" @ok="batchCopy"></ok-cancel>
        </v-dialog>

        <v-dialog v-model="dialogProcessing" width="600" persistent>
            <batch-processing 
                :dialogProcessing.sync="dialogProcessing"
                :processingText="processingText"
                :processingValue="processingValue"
                :processed="processed"
                :failedItems="failedItems"
                :loadingProcessing="loadingProcessing"
                @ok="getHistory"
                ></batch-processing>
        </v-dialog>
    </div>
    
</template>
<script>

import allocationApi from '@/api/allocation'
import commonfn from '@/common/functions'
import awsS3Helper from '@/helpers/aws-s3'
import { getSort, getSearchKeyword, getRowsPerPage, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import updateHistApi from '@/api/updateshistory'

export default {
    components: {
        'team-chips': () => import("@/components/Common/TeamsChip"),
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'batch-processing': () => import('@/components/Common/BatchProcessingContent'),
    },
    data () {
        const routeName = this.$route.name + '_batchcopy'
        const savedSort = getSort(routeName)

        return {
            awsS3Helper,
            commonfn,
            rowsPerPageItems,
            dialogOkCancel: false,
            dialogOkCancelMsg: null,
            loading: false,
            error: false,
            errorMessage: null,
            disabled: false,
            search: getSearchKeyword(routeName),
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            rules: {
                required: value => !!value || 'Required field.',
            }, 
            targetFiscalYear: null,
            targetFiscalPeriodId: null,
            
            userId: null,
            allocation_history: [],

            //for batch processing progress indicator
            processingText: '',
            processingValue: 0,
            loadingProcessing: false,
            dialogProcessing: false,
            processed: 0,
            failed: 0,
            failedItems: [],
        }
    },
    methods: {
        showBatchCopyConfirmation() {
            this.dialogOkCancelMsg = `This action will copy the <b>${this.sourceFiscalYear} Compensation and Benefits</b> of the selected employees to <b>Year ${this.targetFiscalYear}</b>.<br><br>
                                        Only employees with a <b>Not Available</b> compensation status will be included.<br><br>
                                        <b>Would you like to continue?</b>`
            this.dialogOkCancel = true
        },
        async batchCopy() {
            try {
                // Enable batch UI dialog
                this.loadingProcessing = true
                this.dialogProcessing = true
                this.processingValue = 0
                this.failedItems = []
                this.processed = 0
                this.failed = 0

                // Items to process (replace with your own filter if needed)
                const itemsToProcess = this.allocation_history_filtered

                if (!itemsToProcess.length) {
                    this.processingText = "No items to process."
                    this.loadingProcessing = false
                    return
                }

                const total = itemsToProcess.length
                const increment = 100 / total

                this.processingText = `Processing 0 / ${total}...`

                // Process each item
                for (const item of itemsToProcess) {
                    try {
                        var data = {
                            fiscalPeriodId: this.targetFiscalPeriodId,       //target
                            fiscalPeriodIdToCopy: this.sourceFiscalPeriodId, //source
                            userId: item.userId
                        }
                        await allocationApi.copy(data)
                        this.processed++
                    } catch (err) {
                        this.failed++
                        this.failedItems.push({
                            name: `${item.firstName} ${item.lastName}`,
                            userId: item.userId,
                            employeeId: item.employeeId
                        })
                    }

                    // Update progress bar + text
                    this.processingValue = Math.min(
                        this.processingValue + increment,
                        100
                    )

                    this.processingText = `Processing ${this.processed + this.failed} / ${total}...`
                }

                // Final refresh after batch
                await this.getHistory(false)

                this.processingText = `Batch copy: ${this.processed} processed, ${this.failed} failed.`
                await this.updateHistoryComponent(this.processingText)

                this.loadingProcessing = false
            } catch (error) {
                this.processingText = "An unexpected error occurred."
                this.loadingProcessing = false
            }
        },
        async setData(targetData){
            this.allocation_history = []

            this.targetFiscalPeriodId = targetData.fiscalPeriodId
            this.targetFiscalYear = targetData.fiscalYear
            
            console.log(targetData)
            await this.getHistory()
        },
        async updateHistoryComponent(updateAction) {
            try {
                var data = {
                    pageQueryId: this.pageQueryId,
                    updateAction: updateAction
                }
                await updateHistApi.send(data)
            }catch(error){
                this.errorMessage = error
                this.error = true
            }
        },
        async getHistory() {
            try {
                this.loading = true
                const response = await allocationApi.getHistoryByCompany(this.targetFiscalPeriodId)
                if (response && response.status==200){
                    this.allocation_history = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:batchCopyDialog', false)
            this.$emit('send', null)
        },
    },
    created() {},
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            const routeName = this.$route.name + '_batchcopy'
            updateRowsPerPage(routeName, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            const routeName = this.$route.name + '_batchcopy'
            updateSort(routeName, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            const routeName = this.$route.name + '_batchcopy'
            updateSort(routeName, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            const routeName = this.$route.name + '_batchcopy'
            updateSearchKeyword(routeName, newVal)
        }
    },
    computed: {
        allocation_history_filtered() {
            var temp = this.allocation_history

            if (this.sourceFiscalYear) {
                temp = temp.filter(item => item.fiscalYear == this.sourceFiscalYear)
            }

            if (this.search) {
                const searchLower = this.search.toLowerCase()

                temp = temp.filter(item => {
                    const firstNameMatch = item.firstName.toLowerCase().includes(searchLower)
                    const lastNameMatch = item.lastName.toLowerCase().includes(searchLower)
                    const teamsMatch = Array.isArray(item.teams)
                        ? item.teams.join(', ').toLowerCase().includes(searchLower)
                        : false

                    return firstNameMatch || lastNameMatch || teamsMatch
                })
            }

            return temp
        },
        sourceFiscalYear() {
            if (this.allocation_history && this.allocation_history.length)
                return this.allocation_history[0].fiscalYear
        },
        sourceFiscalPeriodId() {
            return this.allocation_history[0].fiscalPeriodId
        },
        noData() {
            return !this.allocation_history_filtered.length
        },
        header_mobile() {
            return [
                {text: 'Employee',align: 'left',value: 'lastName',width: '10%', sortable:true},
                {text: `${this.sourceFiscalYear} Compensation`,align: 'left',value: 'allocationStatus',width: '10%', sortable:true},
            ]
        },
        header() {
            return [
                {text: 'Employee',align: 'left',value: 'lastName',width: '10%', sortable:true},
                {text: 'Teams',align: 'left',value: 'teams',width: '30%', sortable:true},
                {text: 'Employment Status',align: 'left',value: 'employmentStatus',width: '10%', sortable:true},
                {text: `${this.sourceFiscalYear} Compensation`,align: 'left',value: 'allocationStatus',width: '10%', sortable:true},
            ]
        },
        pageQueryId() {
            return `allocation_${this.targetFiscalPeriodId}`
        },
    }
}
</script>